import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { useAdminState } from '../context/AdminContext'
import { apiFetch } from '../api/client'
import { WorkspacesView } from '../views/WorkspacesView'
import { FeatureFlagsView } from '../views/FeatureFlagsView'
import { SystemSettingsView } from '../views/SystemSettingsView'
import { AuthConfigsView } from '../views/AuthConfigsView'
import { UsersView } from '../views/UsersView'
import { ConnectorsView } from '../views/ConnectorsView'
import { ActivityView } from '../views/ActivityView'
import { AdminsView } from '../views/AdminsView'
import { MarketplaceView } from '../views/MarketplaceView'
import { PlatformSkillsView } from '../views/PlatformSkillsView'
import { EndUsersView } from '../views/EndUsersView'
import { ToolSessionsView } from '../views/ToolSessionsView'
import { McpUpstreamsView } from '../views/McpUpstreamsView'
import { McpUpstreamDetailView } from '../views/McpUpstreamDetailView'
import { McpDirectoryView } from '../views/McpDirectoryView'
import './Shell.css'

// ── Nav items ──────────────────────────────────────────────────────────────
// Live sections are active routes. Sections not built yet (T2c) are present in
// the sidebar as visible-but-inactive "coming soon" items — they match the
// full V1 super-admin IA so future sections slot in without sidebar changes.

interface T1NavItem {
  label: string
  path: string
}

const T1_NAV: T1NavItem[] = [
  { label: 'Workspaces',      path: '/workspaces'      },
  { label: 'Plans',           path: '/plans'           },
  { label: 'Users',           path: '/users'           },
  { label: 'End Users',       path: '/end-users'       },
  { label: 'Tool Sessions',   path: '/tool-sessions'   },
  { label: 'Connectors',      path: '/connectors'      },
  { label: 'MCP Upstreams',   path: '/mcp-upstreams'   },
  { label: 'MCP Directory',   path: '/mcp-directory'   },
  { label: 'Marketplace',     path: '/marketplace'     },
  { label: 'Platform Skills', path: '/platform-skills' },
  { label: 'Activity Log',    path: '/activity'        },
  { label: 'Admins',          path: '/admins'          },
  { label: 'Feature Flags',   path: '/feature-flags'   },
  { label: 'System Settings', path: '/system-settings' },
  { label: 'Auth Configs',    path: '/auth-configs'    },
]

// Tranche 2c sections — still inactive.
const COMING_SOON_LABELS = ['Analytics']

async function logout() {
  try {
    await apiFetch('/auth/logout', { method: 'POST' })
  } finally {
    // Hard reload clears all client state (query cache, context, cookies).
    window.location.replace('/')
  }
}

export function Shell() {
  const { identity } = useAdminState()

  return (
    <div className="shell">
      <nav className="shell-sidebar" aria-label="Admin navigation">
        <div className="shell-brand">
          <span className="shell-brand-icon" aria-hidden="true">&#x25C6;</span>
          Symone Admin
        </div>

        <div className="shell-nav-section-label">Platform</div>
        {T1_NAV.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              'shell-nav-link' + (isActive ? ' active' : '')
            }
          >
            {item.label}
          </NavLink>
        ))}

        <div className="shell-nav-section-label shell-nav-section-label--coming">Coming soon</div>
        {COMING_SOON_LABELS.map((label) => (
          <span key={label} className="shell-nav-link shell-nav-link--disabled" aria-disabled="true">
            {label}
          </span>
        ))}

        <div className="shell-spacer" />

        <div className="shell-identity" aria-label="Signed in as">
          <span className="shell-identity-label">Signed in as</span>
          <span className="shell-identity-user">{identity?.user_id ?? '—'}</span>
          <span className="shell-identity-role">{identity?.role ?? ''}</span>
        </div>

        <button
          type="button"
          className="shell-logout-btn"
          onClick={logout}
          aria-label="Sign out"
        >
          Sign out
        </button>
      </nav>

      <main className="shell-main" id="main-content">
        <Routes>
          <Route index element={<Navigate to="/workspaces" replace />} />
          <Route path="/workspaces"      element={<WorkspacesView />} />
          {/* Plan assignment is per workspace; Plans shows the same list. */}
          <Route path="/plans"           element={<WorkspacesView key="plans" title="Plans" />} />
          <Route path="/users"           element={<UsersView />} />
          <Route path="/end-users"       element={<EndUsersView />} />
          <Route path="/tool-sessions"   element={<ToolSessionsView />} />
          <Route path="/connectors"      element={<ConnectorsView />} />
          <Route path="/mcp-upstreams"   element={<McpUpstreamsView />} />
          <Route path="/mcp-upstreams/:slug" element={<McpUpstreamDetailView />} />
          <Route path="/mcp-directory"   element={<McpDirectoryView />} />
          <Route path="/marketplace"     element={<MarketplaceView />} />
          <Route path="/platform-skills" element={<PlatformSkillsView />} />
          <Route path="/activity"        element={<ActivityView />} />
          <Route path="/admins"          element={<AdminsView />} />
          <Route path="/feature-flags"   element={<FeatureFlagsView />} />
          <Route path="/system-settings" element={<SystemSettingsView />} />
          <Route path="/auth-configs"    element={<AuthConfigsView />} />
          {/* Fallback: any unknown path → workspaces */}
          <Route path="*" element={<Navigate to="/workspaces" replace />} />
        </Routes>
      </main>
    </div>
  )
}
